import { createApi } from '@reduxjs/toolkit/query/react';
import customBaseQuery from '@/lib/customBaseQuery';

/*
 * Hospital bed-count data layer -- real backend (dph-portal-api, epic-02-hospital-bed-count-backend).
 * This file used to carry a "MOCK-TO-REAL SWAP" header sketching this exact swap (predicted URL
 * was wrong: `/api/hospitals/${id}/bed-counts`, not the real `/api/hospital-bed-count(/{id})`).
 * That plan is done -- the old in-memory mock module is deleted; this comment documents what's
 * live instead.
 *
 *   HospitalBedCount.container                                allBedCounts <- HospitalList.container
 *     useBedCountsByOrgIdQuery({ id, accessToken }) ──┐          (see that file's own header comment)
 *     useSaveBedCountsMutation() ──────────────────────┤
 *     useBedCountActivityQuery(id) ────────────────────┤  (story-08)
 *     usePostBedCountCommentMutation() ─────────────────┤  (story-08)
 *                                                       ▼
 *                       hospitalApi.ts (query)
 *                                    │
 *                                    ▼
 *      GET  /api/hospital-bed-count/{id}          POST /api/hospital-bed-count
 *      GET  /api/hospital-bed-count/{id}/activity  POST .../{id}/comments  (story-08)
 *                                    │
 *                                    ▼
 *            dph-portal-api -- healthcare_facility ⟕ healthcare_bed_count
 *                                                  ⟕ healthcare_bed_count_audit ⟕ post_comment
 *
 * CATEGORY-COUNT CAVEAT -- re-check before this file's save path is deployed anywhere new:
 * saveBedCounts's payload can carry all 15 HospitalBedCategory values (story-05, closed). Confirm
 * the target backend's PostBedCountsSchema accepts all 15 (dph-portal-api epic-02-hospital-bed-
 * count-backend/story-03-bed-count-category-parity) before deploying this file's save path there --
 * see .specs/epic-01-hospital-feature/story-06-hospital-bed-count-backend-swap.md > ## Boundaries.
 */

const hospitalBedCountBaseUrl = '/api/hospital-bed-count';

export const hospitalApi = createApi({
  reducerPath: 'hospitalApi',
  baseQuery: customBaseQuery,
  tagTypes: ['HospitalBedCounts', 'HospitalBedCountList', 'HospitalBedCountActivity'],
  endpoints: (builder) => ({
    bedCountsByOrgId: builder.query<
      HospitalBedCountsRecord,
      { id: number; accessToken?: string | null }
    >({
      query: ({ id, accessToken }) => ({
        url: `${hospitalBedCountBaseUrl}/${id}`,
        params: accessToken ? { access_token: accessToken } : undefined,
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'HospitalBedCounts', id }],
    }),
    saveBedCounts: builder.mutation<HospitalBedCountsRecord, PostBedCountsPayload>({
      query: (payload) => ({
        url: hospitalBedCountBaseUrl,
        method: 'POST',
        params: payload?.accessToken ? { access_token: payload.accessToken } : undefined,
        body: payload,
      }),
      invalidatesTags: (_result, _error, payload) => [
        'HospitalBedCountList',
        { type: 'HospitalBedCounts', id: payload.facilityId },
        // A save writes the audit rows story-08's activity feed reads -- without this, the new
        // entry would only appear after an unrelated refetch (e.g. navigating away and back).
        { type: 'HospitalBedCountActivity', id: payload.facilityId },
      ],
    }),
    allBedCounts: builder.query<HospitalBedCountsTableResponse, HospitalBedCountListSearchParams>({
      query: (params) => ({ url: `${hospitalBedCountBaseUrl}/`, params }),
      providesTags: ['HospitalBedCountList'],
    }),
    // GET /api/hospital-bed-count/{id}/activity -- dph-portal-api epic-02, closed. Returns raw,
    // ungrouped audit rows; HospitalBedCount/deriveActivityFeedData.ts groups them client-side.
    bedCountActivity: builder.query<HospitalBedCountActivityResponse, number>({
      query: (facilityId) => ({ url: `${hospitalBedCountBaseUrl}/${facilityId}/activity` }),
      providesTags: (_result, _error, facilityId) => [
        { type: 'HospitalBedCountActivity', id: facilityId },
      ],
    }),
    // POST /api/hospital-bed-count/{id}/comments -- facilityId only addresses the URL; the
    // request body carries just `comment` (PostBedCountCommentSchema), matching the real schema.
    postBedCountComment: builder.mutation<HospitalBedCountCommentRow, PostBedCountCommentPayload>({
      query: ({ facilityId, comment }) => ({
        url: `${hospitalBedCountBaseUrl}/${facilityId}/comments`,
        method: 'POST',
        body: { comment },
      }),
      invalidatesTags: (_result, _error, { facilityId }) => [
        { type: 'HospitalBedCountActivity', id: facilityId },
      ],
    }),
    // PUT /api/hospital-bed-count/request-update/{id} -- story-12. Triggers the backend's magic-URL
    // email; no body, matching organizationApi's requestUpdateInfo/requestUpdateInventory shape.
    requestUpdateBedCount: builder.mutation<string, number>({
      query: (id) => ({ url: `${hospitalBedCountBaseUrl}/request-update/${id}`, method: 'PUT' }),
    }),
  }),
});

export const {
  useBedCountsByOrgIdQuery,
  useSaveBedCountsMutation,
  useAllBedCountsQuery,
  useBedCountActivityQuery,
  usePostBedCountCommentMutation,
  useRequestUpdateBedCountMutation,
} = hospitalApi;
